/**
 * The look of an event's room: its linen, chairs, flowers, tableware, walls
 * and floor, and the pictures they were taken from.
 *
 * Set once for the event (`Event.decor`) and, where one floor plan differs —
 * a cocktail hour in another linen — overridden on that plan
 * (`FloorPlan.decor`, the same fields, only those it changes). The realistic
 * 3D view, the AI Render Studio and the client portal's 3D all read the
 * look resolved from the two (`resolveDecor`), so a choice is made once and
 * shows everywhere.
 *
 * Every field may be left out. Left out, it comes from the event's pictures
 * when there are any (`DecorPalette`, the colours taken from them), else from
 * the default the 3D view always had.
 */
/** The four groups of pictures an event's look is taken from. */
export const DECOR_PICTURE_KINDS = ['chairs', 'flowers', 'hall', 'tableware'];
/**
 * The record the pictures are filed on: the event's id under a type of their
 * own, never `EVENT`. The file library shows a client every file anchored to
 * an `event` they are on; these are the office's reference pictures, so they
 * are anchored to `event_decor`, which only the office may open.
 */
export const DECOR_PICTURE_ENTITY_TYPE = 'EVENT_DECOR';
/** `documentType` of each group's pictures. */
export const DECOR_PICTURE_DOCUMENT_TYPES = {
    chairs: 'DECOR_CHAIRS',
    flowers: 'DECOR_FLOWERS',
    hall: 'DECOR_HALL',
    tableware: 'DECOR_TABLEWARE',
};
export const DECOR_PICTURE_LABELS = {
    chairs: 'Chairs',
    flowers: 'Flower arrangements',
    hall: 'The hall',
    tableware: 'Plates, cutlery & glasses',
};
/** How many pictures a group holds. */
export const MAX_DECOR_PICTURES_PER_KIND = 8;
/** The largest picture kept — the AI reads pictures of about 3.75 MB at most. */
export const MAX_DECOR_PICTURE_BYTES = 3_700_000;
/** Picture types a group takes: what a browser draws and the AI reads. */
export const DECOR_PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const CHAIR_STYLES = ['roundback', 'chiavari', 'crossback', 'ghost', 'banquet'];
export const CHAIR_STYLE_LABELS = {
    roundback: 'Round back',
    chiavari: 'Chiavari',
    crossback: 'Cross back',
    ghost: 'Ghost (clear)',
    banquet: 'Banquet (upholstered)',
};
export const CENTERPIECE_STYLES = ['low', 'tall', 'candles', 'none'];
export const CENTERPIECE_STYLE_LABELS = {
    low: 'Low arrangement',
    tall: 'Tall arrangement',
    candles: 'Candles only',
    none: 'Nothing',
};
export const CUTLERY_FINISHES = ['silver', 'gold', 'black'];
export const CUTLERY_FINISH_LABELS = {
    silver: 'Silver',
    gold: 'Gold',
    black: 'Black',
};
export const GLASS_STYLES = ['clear', 'gold_rim', 'tinted'];
export const GLASS_STYLE_LABELS = {
    clear: 'Clear',
    gold_rim: 'Gold rim',
    tinted: 'Coloured',
};
export const DECOR_FLOOR_FINISHES = ['stone', 'wood', 'marble', 'carpet'];
export const DECOR_FLOOR_FINISH_LABELS = {
    stone: 'Stone',
    wood: 'Wood',
    marble: 'Marble',
    carpet: 'Carpet',
};
export const NAPKIN_FOLDS = ['flat', 'standing', 'ring'];
export const NAPKIN_FOLD_LABELS = {
    flat: 'Folded flat on the plate',
    standing: 'Standing fold',
    ring: 'Rolled in a ring',
};
export const GLASS_SHAPES = ['classic', 'goblet', 'stemless'];
export const GLASS_SHAPE_LABELS = {
    classic: 'Classic stemware',
    goblet: 'Goblets',
    stemless: 'Stemless',
};
export const LINEN_FINISHES = ['plain', 'satin', 'sequin', 'velvet'];
export const LINEN_FINISH_LABELS = {
    plain: 'Plain (polyester / linen)',
    satin: 'Satin',
    sequin: 'Sequin',
    velvet: 'Velvet',
};
export const CANDLE_STYLES = ['taper', 'candelabra', 'votives', 'none'];
export const CANDLE_STYLE_LABELS = {
    taper: 'Taper candlesticks',
    candelabra: 'Candelabra',
    votives: 'Votives',
    none: 'No candles',
};
export const CANDLE_FINISHES = ['gold', 'silver', 'black', 'crystal'];
export const CANDLE_FINISH_LABELS = {
    gold: 'Gold',
    silver: 'Silver',
    black: 'Black',
    crystal: 'Crystal',
};
export const COCKTAIL_TABLE_STYLES = ['spandex', 'draped', 'bare'];
export const COCKTAIL_TABLE_STYLE_LABELS = {
    spandex: 'Spandex cover',
    draped: 'Draped & tied',
    bare: 'Bare',
};
export const BAR_STYLES = ['wood', 'white', 'marble', 'mirror', 'led'];
export const BAR_STYLE_LABELS = {
    wood: 'Wood',
    white: 'White lacquer',
    marble: 'Marble',
    mirror: 'Mirror',
    led: 'Lit (LED)',
};
export const DANCE_FLOOR_STYLES = ['checker', 'white_gloss', 'black_gloss', 'wood', 'led'];
export const DANCE_FLOOR_STYLE_LABELS = {
    checker: 'Black & white',
    white_gloss: 'White gloss',
    black_gloss: 'Black gloss',
    wood: 'Wood parquet',
    led: 'LED panels',
};
export const CHUPPAH_STYLES = ['floral', 'draped', 'birch', 'modern'];
export const CHUPPAH_STYLE_LABELS = {
    floral: 'Floral canopy',
    draped: 'Draped fabric',
    birch: 'Birch wood',
    modern: 'Modern frame',
};
export const LOUNGE_STYLES = ['modern', 'tufted', 'velvet'];
export const LOUNGE_STYLE_LABELS = {
    modern: 'Modern',
    tufted: 'Tufted (chesterfield)',
    velvet: 'Velvet',
};
/** How many flower colours an arrangement mixes. */
export const MAX_FLOWER_COLORS = 6;
/** The look the 3D view had before any of this — what an event with nothing set shows. */
export const DEFAULT_DECOR = {
    linenColor: '#f7f6f2',
    napkinColor: '#f7f6f2',
    chairStyle: 'roundback',
    chairColor: '#2b2522',
    cushionColor: '#f2efe9',
    centerpiece: 'low',
    flowerColors: ['#fbf8f2', '#ffffff', '#f1e6dc'],
    greeneryColor: '#6f8662',
    charger: true,
    chargerColor: '#e3d3b0',
    plateColor: '#fbfaf7',
    cutlery: 'silver',
    glass: 'clear',
    glassColor: '#ffffff',
    wallColor: null,
    floorColor: null,
    floorFinish: 'stone',
    linenFinish: 'plain',
    napkinFold: 'flat',
    glassShape: 'classic',
    candleStyle: 'taper',
    candleFinish: null,
    cocktailStyle: 'spandex',
    cocktailColor: null,
    barStyle: 'wood',
    barColor: null,
    danceFloorStyle: 'checker',
    chuppahStyle: 'floral',
    chuppahFabricColor: '#fbf8f2',
    chuppahFlowerColors: [],
    loungeStyle: 'modern',
    loungeColor: '#efe9de',
};
/** Linen colours offered as one-press choices beside the colour picker. */
export const LINEN_PRESETS = [
    { label: 'White', color: '#f7f6f2' },
    { label: 'Ivory', color: '#f1eadb' },
    { label: 'Blush', color: '#efd3cf' },
    { label: 'Sage', color: '#c5d3bf' },
    { label: 'Dusty blue', color: '#b9c8d8' },
    { label: 'Champagne', color: '#e8dcc2' },
    { label: 'Navy', color: '#2d3a5c' },
    { label: 'Black', color: '#222226' },
];
//# sourceMappingURL=decor.js.map